import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DateTime } from 'luxon';
import { Observable, throwError } from 'rxjs';
import { DATETIME_FORMAT, DATE_ISO_FORMAT } from '../constants';
import { environment } from '../../../environments/environment';
import { showAlert } from './alert';

export function toTitleCase(str: string) {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function formatDate(date: string | Date, format = DATETIME_FORMAT) {
    if (typeof date === 'string') {
        return DateTime.fromISO(date).toFormat(format);
    } else {
        return DateTime.fromJSDate(date).toFormat(format);
    }
}

export function formatISODate(date: string | Date) {
    return formatDate(date, DATE_ISO_FORMAT);
}

export function getMonthStart(date: string | Date, format = DATE_ISO_FORMAT) {
    if (typeof date === 'string') {
        return DateTime.fromISO(date).startOf('month').toFormat(format);
    } else {
        return DateTime.fromJSDate(date).startOf('month').toFormat(format);
    }
}

export function getMonthEnd(date: string | Date, format = DATE_ISO_FORMAT) {
    if (typeof date === 'string') {
        return DateTime.fromISO(date).endOf('month').toFormat(format);
    } else {
        return DateTime.fromJSDate(date).endOf('month').toFormat(format);
    }
}

export function formatNumber(number: number, separator = '.', decimals = 0) {
    if (!number) {
        return '0';
    }
    return number
        .toFixed(decimals)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export function getOffset(page: number, limit: number) {
    return (page - 1) * limit;
}

export function groupBy(array: any[], key: string) {
    return array.reduce((rv, x) => {
        if (x[key] instanceof Date) {
            x[key] = x[key].toISOString().split('T')[0];
        }
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
}

export function errorHandler(error: HttpErrorResponse) {
    return throwError(() => new Error(error?.error?.message || 'server error.'));
}

export function ifHasErrors(form: FormGroup, control: string, type: string) {
    return form.get(control)?.touched && form.get(control)?.hasError(type);
}

// AOT compilation support
export function httpTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export function sortByKey<T extends Record<string, unknown>>(
    array: T[],
    key: keyof T,
    direction: 'asc' | 'desc'
) {
    const indexedArray = array.map((item, index) => ({ item, index }));

    indexedArray.sort((a, b) => {
        if (a.item[key] < b.item[key]) {
            return direction === 'asc' ? -1 : 1;
        } else if (a.item[key] > b.item[key]) {
            return direction === 'asc' ? 1 : -1;
        } else {
            return 0;
        }
    });

    const sortedArray = indexedArray.map(({ item }) => item);
    const originalIndices = indexedArray.map(({ index }) => index);

    return { sortedArray, originalIndices };
}

export function getHeaders(): HttpHeaders {
  const tokenKey = environment.accessTokenName;
  return new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem(tokenKey)}`
  });
}

export function handleResponse<T>(response: T & { code: number; message?: string }): T {
  if (response.code !== 0) {
    throw new Error(response.message);
  }
  return response;
}

export function handleError(error: any): Observable<never> {
  console.error('API Error:', error);
  return throwError(() => new Error(error.error?.message || 'Error'));
}

export function toQueryString(params: Record<string, any>): string {
  return Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined) // ⬅️ filter null/undefined
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
  showAlert({ type, title, text }).then();
}

export function getYesterday(today: number = 0): string {
  const yesterday = new Date();

  if (today === 1) {
    yesterday.setDate(yesterday.getDate() - 1);
  }

  const day = String(yesterday.getDate()).padStart(2, '0');
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const year = yesterday.getFullYear();

  return `${year}-${month}-${day}`;
}

export function getUtcDateRangeForLocalDate(localDate: string): { startUtc: string, endUtc: string } {
  const localStart = new Date(`${localDate}T00:00:00+07:00`);
  const localEnd = new Date(`${localDate}T23:59:59+07:00`);

  const formatDate = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  };

  return {
    startUtc: formatDate(localStart),
    endUtc: formatDate(localEnd),
  };
}
